import { useMemo, useState } from "react";
import { clsx } from "clsx";
import { Paperclip } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { FileDropZone } from "@/components/ui/FileDropZone";
import { MultiSelect } from "@/components/ui/MultiSelect";
import { Spinner } from "@/components/ui/Spinner";
import { EmptyState, SectionCard, SubTabs } from "@/features/power_projects/components/ui";
import * as api from "../api";
import { useLedMutation, useMemos } from "../hooks";
import type { LedParcel, MemoRow } from "../types";
import { ErrorLine, Field, FileChips, INPUT, INPUT_SM, SideTag, fmtDateTime } from "./common";

interface Props {
  leadershipId: string;
  internal: boolean;
  contractorName: string;
  parcels: LedParcel[];
}

type Filter = "all" | "unread" | "mine" | "theirs";

/** Działki do wyboru: szuka się po numerze ALBO po nazwisku właściciela, bo notatkę
 *  pisze się „w sprawie Kowalskiego", a nie „w sprawie 121803_5.0007.1994/2". */
function parcelOptions(parcels: LedParcel[]) {
  return parcels.map((p) => {
    const owners = p.owners.map((o) => o.full_name).join(", ");
    return {
      value: p.id,
      label: owners ? `${p.teryt} · ${owners}` : `${p.teryt} · bez stron`,
    };
  });
}

function ParcelChips({ memo }: { memo: MemoRow }) {
  if (memo.parcels.length === 0) return null;
  return (
    <span className="flex flex-wrap gap-1">
      {memo.parcels.map((p) => (
        <span
          key={p.id}
          className="border border-[var(--color-border)] bg-[var(--color-bg-elevated)] px-1.5 py-px font-mono text-[10px]"
        >
          {p.teryt}
        </span>
      ))}
    </span>
  );
}

function MemoCard({
  leadershipId,
  memo,
  contractorName,
}: {
  leadershipId: string;
  memo: MemoRow;
  contractorName: string;
}) {
  const read = useLedMutation(leadershipId, () => api.readMemo(leadershipId, memo.id), ["memos"]);
  const edit = useLedMutation(
    leadershipId,
    (body: { subject: string; body: string }) => api.editMemo(leadershipId, memo.id, body),
    ["memos"],
  );
  const [editing, setEditing] = useState(false);
  const [subject, setSubject] = useState(memo.subject);
  const [body, setBody] = useState(memo.body);
  const unread = !memo.read_by_me;
  return (
    <article
      className={clsx("card p-3.5", unread && "bg-amber-50/70 dark:bg-amber-950/25")}
      style={{
        borderLeft: `3px solid ${
          unread ? "var(--color-primary)" : memo.side === "gk" ? "var(--color-border-strong)" : "#1d4ed8"
        }`,
      }}
    >
      <header className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <span className="font-mono text-[11px] font-bold text-[var(--color-text-muted)]">{memo.label}</span>
        <h3 className="flex-1 text-[14px] font-bold leading-tight">{memo.subject}</h3>
        {memo.can_edit && !editing && (
          <Button size="sm" variant="ghost" onClick={() => setEditing(true)}>
            popraw
          </Button>
        )}
        {unread && (
          <Button size="sm" disabled={read.isPending} onClick={() => read.mutate()}>
            Oznacz jako odczytane
          </Button>
        )}
      </header>
      <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-[var(--color-text-muted)]">
        <SideTag side={memo.side} contractorName={contractorName} />
        <span>{memo.author_label}</span>
        <span>·</span>
        <span>{fmtDateTime(memo.sent_at)}</span>
        {memo.edited_at && <span>· poprawiona {fmtDateTime(memo.edited_at)} (wersja {memo.versions_count + 1})</span>}
        {memo.milestone_label && <span>· etap {memo.milestone_label}</span>}
        {memo.files.length > 0 && (
          <span className="inline-flex items-center gap-1">
            · <Paperclip size={11} /> {memo.files.length}
          </span>
        )}
        <ParcelChips memo={memo} />
      </div>
      {editing ? (
        <div className="mt-2 space-y-2">
          <input className={clsx(INPUT, "font-semibold")} value={subject} onChange={(e) => setSubject(e.target.value)} />
          <textarea className={clsx(INPUT, "min-h-[6rem]")} value={body} onChange={(e) => setBody(e.target.value)} />
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>
              Anuluj
            </Button>
            <Button
              size="sm"
              disabled={edit.isPending}
              onClick={async () => {
                await edit.mutateAsync({ subject, body });
                setEditing(false);
              }}
            >
              Zapisz poprawkę
            </Button>
          </div>
          <ErrorLine error={edit.error} />
        </div>
      ) : (
        <p className="mt-2 whitespace-pre-wrap text-[13px] leading-relaxed">{memo.body}</p>
      )}
      <FileChips files={memo.files} className="mt-2" />
      {memo.read_by.length > 0 && (
        <footer className="mt-2 border-t border-[var(--color-border-muted)] pt-1.5 text-[11px] text-[var(--color-text-muted)]">
          odczytali: {memo.read_by.map((r) => `${r.label} (${fmtDateTime(r.read_at)})`).join(", ")}
        </footer>
      )}
      <ErrorLine error={read.error} />
    </article>
  );
}

/** Notatki służbowe — jedyny kanał rozmowy w projekcie: numerowane, z załącznikami,
 *  z jawnym „Oznacz jako odczytane”. Firmowe niezmienne; nasze poprawiane do 24 h. */
export function MemosTab({ leadershipId, internal, contractorName, parcels }: Props) {
  const { data, isLoading, error } = useMemos(leadershipId);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [chosen, setChosen] = useState<string[]>([]);
  const [files, setFiles] = useState<File[]>([]);
  const [composing, setComposing] = useState(false);
  const [filter, setFilter] = useState<Filter>("all");
  const [q, setQ] = useState("");
  const options = useMemo(() => parcelOptions(parcels), [parcels]);
  const create = useLedMutation(
    leadershipId,
    () => api.createMemo(leadershipId, { subject, body, parcels: chosen, files }),
    ["memos"],
  );
  if (isLoading) return <div className="flex h-40 items-center justify-center"><Spinner /></div>;
  if (error) return <ErrorLine error={error} />;
  const all = [...(data ?? [])].sort((a, b) => b.number - a.number);
  const mineSide = internal ? "gk" : "contractor";
  const needle = q.trim().toLowerCase();
  const memos = all
    .filter((m) =>
      filter === "unread"
        ? !m.read_by_me
        : filter === "mine"
          ? m.side === mineSide
          : filter === "theirs"
            ? m.side !== mineSide && m.side !== "system"
            : true,
    )
    .filter(
      (m) =>
        !needle ||
        m.subject.toLowerCase().includes(needle) ||
        m.body.toLowerCase().includes(needle) ||
        m.parcels.some((p) => p.teryt.toLowerCase().includes(needle)),
    );
  const unread = all.filter((m) => !m.read_by_me).length;

  return (
    <div className="space-y-3">
      {composing ? (
        <SectionCard title="Nowa notatka służbowa" bodyClassName="p-4 space-y-3">
          <Field label="Temat">
            <input className={INPUT} autoFocus value={subject} onChange={(e) => setSubject(e.target.value)} />
          </Field>
          <Field label="Dotyczy działek (szukaj po numerze albo nazwisku)">
            <MultiSelect
              options={options}
              value={chosen}
              onChange={setChosen}
              placeholder="— nie dotyczy konkretnej działki —"
              searchPlaceholder="Numer działki albo nazwisko właściciela..."
              chipsMaxHeight={96}
            />
          </Field>
          <Field label="Treść">
            <textarea className={clsx(INPUT, "min-h-[7rem]")} value={body} onChange={(e) => setBody(e.target.value)} />
          </Field>
          <FileDropZone files={files} onFilesChange={setFiles} multiple label="Załączniki (można kilka)" />
          <p className="text-[11px] text-[var(--color-text-muted)]">
            {internal
              ? "Notatkę można poprawić do 24 godzin od wysłania — historia wersji zostaje."
              : "Wysłanej notatki nie da się zmienić — to dokument. Poprawkę wyślij jako kolejną notatkę."}
          </p>
          <ErrorLine error={create.error} />
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="ghost" onClick={() => setComposing(false)}>
              Anuluj
            </Button>
            <Button
              size="sm"
              disabled={!subject.trim() || !body.trim() || create.isPending}
              onClick={async () => {
                await create.mutateAsync();
                setSubject("");
                setBody("");
                setFiles([]);
                setChosen([]);
                setComposing(false);
              }}
            >
              Wyślij notatkę
            </Button>
          </div>
        </SectionCard>
      ) : (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <SubTabs<Filter>
            active={filter}
            onChange={setFilter}
            tabs={[
              { key: "all", label: "Wszystkie", count: all.length || undefined, countTone: "blue" },
              { key: "unread", label: "Nieodczytane", count: unread || undefined, countTone: "warn" },
              { key: "mine", label: "Nasze" },
              { key: "theirs", label: internal ? "Od podwykonawcy" : "Od GK ELEKTIM" },
            ]}
          />
          <span className="flex items-center gap-2">
            <input
              className={clsx(INPUT_SM, "w-56")}
              placeholder="Szukaj w notatkach"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
            <Button size="sm" onClick={() => setComposing(true)}>
              Nowa notatka
            </Button>
          </span>
        </div>
      )}
      <div className="space-y-2">
        {memos.map((m) => (
          <MemoCard key={m.id} leadershipId={leadershipId} memo={m} contractorName={contractorName} />
        ))}
        {memos.length === 0 && (
          <div className="card">
            <EmptyState>{all.length === 0 ? "Brak notatek służbowych" : "Nic nie pasuje do filtra"}</EmptyState>
          </div>
        )}
      </div>
    </div>
  );
}
